import { useEffect, useMemo, useRef } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type { CompanyApplicationsListScope } from '@/lib/companyApplicationsList';
import {
  collectApplicationsFromListCaches,
  collectCompanyListPageOverlay,
  COMPANY_APPLICATIONS_FETCH_SIZE,
  mergeApplicationListsPreferNewest,
  mergeFreshCompanyListPage,
  mergeServerListPageWithOverlay,
  orderApplicationsForServerList,
  preferNewerApplicationRow,
  type CompanyApplicationsPage,
} from '@/lib/companyApplicationsList';

export type { CompanyApplicationsPage };
import { api } from '@/services/api';
import { LIST_GC_TIME, LIST_STALE_TIME } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import { filterApplicationQueue } from '@/lib/applicationArchive';
import { shouldShowApplicationsListLoading } from '@/lib/applicationListLoading';
import {
  createApplicationListSync,
  resolveApplicationPositionId,
} from '@/lib/applicationListSync';
import { useCompanyApplicationsListCache } from '@/hooks/useCompanyApplicationsListCache';
import { getRecentSettledRetainIds } from '@/lib/recentSettledApplications';
import { isActiveCountEndpointUnavailable } from '@/lib/activeCountAvailability';
import { isArchivedCountEndpointUnavailable } from '@/lib/archivedCountAvailability';
import type { Application, JobPosition, JobPosting } from '@/types';

export { resolveApplicationPositionId };

const listQueryOptions = {
  staleTime: LIST_STALE_TIME,
  gcTime: LIST_GC_TIME,
  refetchOnWindowFocus: false as const,
  refetchOnMount: false as const,
};

function normalizePositionTitle(title: string | undefined): string {
  return (title ?? '').trim().toLowerCase();
}

/** Slice of the company-wide applications cache for one position queue. */
export function filterApplicationsForPosition(
  applications: Application[] | undefined,
  positionId: string | undefined,
  archived: boolean,
  positionTitle?: string,
): Application[] | undefined {
  if (!applications || !positionId) return undefined;

  const byId = applications.filter((a) => resolveApplicationPositionId(a) === positionId);
  let matched = byId;
  if (matched.length === 0 && positionTitle) {
    const titleKey = normalizePositionTitle(positionTitle);
    matched = applications.filter(
      (a) => normalizePositionTitle(a.positionTitle) === titleKey,
    );
  }

  return filterApplicationQueue(matched, archived);
}

/** Prefer a non-empty API result; never let `[]` hide a populated company-cache slice. */
export function mergePositionApplicationsList(
  fetched: Application[] | undefined,
  seeded: Application[] | undefined,
): Application[] {
  const fromApi = fetched ?? [];
  const fromCompany = seeded ?? [];
  if (fromApi.length > 0) return fromApi;
  return fromCompany;
}

/** Union per-position API rows with company-cache slice (newest updatedAt wins). */
export function unionPositionApplicationsList(
  fetched: Application[] | undefined,
  seeded: Application[] | undefined,
): Application[] {
  return mergeApplicationListsPreferNewest(seeded ?? [], fetched ?? []);
}

function applicationRowsEquivalent(
  a: Application[] | undefined,
  b: Application[] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  const byId = new Map(a.map((row) => [row.id, row] as const));
  for (const row of b) {
    const prev = byId.get(row.id);
    if (!prev) return false;
    if (prev.updatedAt !== row.updatedAt || prev.state !== row.state) return false;
  }
  return true;
}

export function usePositionsQuery(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.positions(companyId ?? ''),
    queryFn: () => api.positions.list(companyId!),
    enabled: !!companyId,
    ...listQueryOptions,
  });
}

export function useCompanyTeamsQuery(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.companyTeams(companyId ?? ''),
    queryFn: () => api.companyTeams.list(companyId!),
    enabled: !!companyId,
    ...listQueryOptions,
  });
}

export function useApplicationsFilteredCountQuery(
  companyId: string | undefined,
  scope: CompanyApplicationsListScope,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.applicationsFilteredCount(companyId ?? '', scope),
    queryFn: () => api.applications.filteredCount(companyId!, {
      archived: scope.archived,
      state: scope.state,
      jobPositionId: scope.jobPositionId,
    }),
    enabled: !!companyId && enabled,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useActiveApplicationsCountQuery(companyId: string | undefined) {
  const endpointUnavailable = companyId
    ? isActiveCountEndpointUnavailable(companyId)
    : false;

  return useQuery({
    queryKey: queryKeys.applicationsActiveCount(companyId ?? ''),
    queryFn: () => api.applications.activeCount(companyId!),
    enabled: !!companyId && !endpointUnavailable,
    retry: false,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useArchivedApplicationsCountQuery(companyId: string | undefined) {
  const endpointUnavailable = companyId
    ? isArchivedCountEndpointUnavailable(companyId)
    : false;

  return useQuery({
    queryKey: queryKeys.applicationsArchivedCount(companyId ?? ''),
    queryFn: () => api.applications.archivedCount(companyId!),
    enabled: !!companyId && !endpointUnavailable,
    retry: false,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

export function usePostingsAllQuery(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const positionsQuery = usePositionsQuery(companyId);

  return useQuery({
    queryKey: queryKeys.postingsAll(companyId ?? ''),
    queryFn: async () => {
      const positions =
        queryClient.getQueryData<JobPosition[]>(queryKeys.positions(companyId!)) ?? [];
      return api.postings.listAll(companyId!, positions);
    },
    enabled: !!companyId && positionsQuery.isSuccess,
    ...listQueryOptions,
  });
}

export function usePositionQuery(companyId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.position(companyId ?? '', positionId ?? ''),
    queryFn: async () => {
      const position = await api.positions.get(companyId!, positionId!);
      return position ?? null;
    },
    enabled: !!companyId && !!positionId,
    ...listQueryOptions,
  });
}

export function usePositionPostingsQuery(
  companyId: string | undefined,
  positionId: string | undefined,
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.positionPostings(companyId ?? '', positionId ?? ''),
    queryFn: async () => {
      const items = await api.postings.listByPosition(companyId!, positionId!);
      if (items.length > 0) return items;

      const cachedAll = queryClient.getQueryData<JobPosting[]>(
        queryKeys.postingsAll(companyId!),
      );
      const fromCache = cachedAll?.filter((p) => p.positionId === positionId) ?? [];
      return fromCache.length > 0 ? fromCache : items;
    },
    enabled: !!companyId && !!positionId,
    staleTime: 0,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

/**
 * Loads applications for a single position (active queue by default).
 * Reuses the company-wide applications cache when available so the job-position
 * tab does not flash empty/loading after visiting /applications.
 */
export function usePositionApplicationsFromCache(
  companyId: string | undefined,
  positionId: string | undefined,
  options?: { archived?: boolean; enabled?: boolean; positionTitle?: string },
) {
  const queryClient = useQueryClient();
  const archived = options?.archived === true;
  const enabled = !!companyId && !!positionId && options?.enabled !== false;
  const companyQueryKey = queryKeys.applications(companyId ?? '', archived);

  const resolvedPositionTitle = useMemo(() => {
    if (options?.positionTitle) return options.positionTitle;
    const positions = queryClient.getQueryData<JobPosition[]>(
      queryKeys.positions(companyId ?? ''),
    );
    return positions?.find((p) => p.id === positionId)?.title;
  }, [options?.positionTitle, queryClient, companyId, positionId]);

  const { applications: activeCompanyList } = useCompanyApplicationsListCache(
    companyId,
    false,
  );
  const { applications: archivedCompanyList } = useCompanyApplicationsListCache(
    companyId,
    true,
  );

  const listCacheSeed = useMemo(() => {
    if (!companyId) return [];
    return collectApplicationsFromListCaches(queryClient, companyId, {
      archived,
      orderBy: 'updatedAt',
    });
  }, [archived, companyId, queryClient, activeCompanyList, archivedCompanyList]);

  const companyListData = useMemo(() => {
    if (!archived) {
      return mergeApplicationListsPreferNewest(activeCompanyList, listCacheSeed);
    }
    const legacy = queryClient.getQueryData<Application[]>(companyQueryKey);
    return mergeApplicationListsPreferNewest(
      archivedCompanyList,
      listCacheSeed,
      legacy ?? [],
    );
  }, [
    archived,
    activeCompanyList,
    archivedCompanyList,
    listCacheSeed,
    queryClient,
    companyQueryKey,
  ]);

  const seededFromCompany = useMemo(
    () => filterApplicationsForPosition(
      companyListData,
      positionId,
      archived,
      resolvedPositionTitle,
    ),
    [companyListData, positionId, archived, resolvedPositionTitle],
  );

  const positionApplicationsQuery = useQuery({
    queryKey: queryKeys.positionApplications(companyId ?? '', positionId ?? '', archived),
    queryFn: async () => {
      const companyCache = queryClient.getQueryData<Application[]>(
        queryKeys.applications(companyId!, archived),
      );
      const seed = filterApplicationsForPosition(
        companyCache,
        positionId,
        archived,
        resolvedPositionTitle,
      );

      try {
        const items = await api.applications.listByPosition(companyId!, positionId!, {
          archived,
          orderBy: 'updatedAt',
        });
        if (!archived) {
          queryClient.setQueryData<Application[] | undefined>(
            queryKeys.applications(companyId!, false),
            (prev) => {
              if (!prev) return prev;
              const byId = new Map(prev.map((a) => [a.id, a] as const));
              for (const a of items) {
                const existing = byId.get(a.id);
                byId.set(
                  a.id,
                  existing ? preferNewerApplicationRow(existing, a) : a,
                );
              }
              return Array.from(byId.values());
            },
          );
        }
        const merged = unionPositionApplicationsList(items, seed);
        const filtered = filterApplicationQueue(merged, archived);
        // Per-position API can return [] while company cache has rows (id/title drift).
        if (filtered.length === 0 && seed && seed.length > 0) {
          return seed;
        }
        return filtered;
      } catch {
        if (!archived) {
          if (seed && seed.length > 0) return seed;
          throw new Error('Failed to load applications');
        }
        return filterApplicationQueue(seed ?? [], true);
      }
    },
    enabled,
    ...listQueryOptions,
    placeholderData: (previous) =>
      unionPositionApplicationsList(previous, seededFromCompany),
  });

  const refetchPositionApplications = positionApplicationsQuery.refetch;
  const emptyPositionRefetchInFlight = useRef(false);

  // Merge company-cache slice into position cache (never replace a fuller API result).
  useEffect(() => {
    if (!enabled || !companyId || !positionId || archived) return;
    const seed = filterApplicationsForPosition(
      activeCompanyList,
      positionId,
      false,
      resolvedPositionTitle,
    );
    if (!seed?.length) return;
    queryClient.setQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, false),
      (prev) => {
        const next = unionPositionApplicationsList(prev, seed);
        return applicationRowsEquivalent(prev, next) ? prev! : next;
      },
    );
  }, [
    enabled,
    companyId,
    positionId,
    archived,
    activeCompanyList,
    resolvedPositionTitle,
    queryClient,
  ]);

  // Company cache arrived after a cached empty position slice — refresh the query.
  useEffect(() => {
    if (!enabled || !companyId || !positionId || archived) return;
    if (!seededFromCompany?.length) return;
    const cached = queryClient.getQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, false),
    );
    if (cached && cached.length > 0) {
      emptyPositionRefetchInFlight.current = false;
      return;
    }
    if (emptyPositionRefetchInFlight.current) return;
    emptyPositionRefetchInFlight.current = true;
    void refetchPositionApplications().finally(() => {
      emptyPositionRefetchInFlight.current = false;
    });
  }, [
    enabled,
    companyId,
    positionId,
    archived,
    seededFromCompany?.length,
    queryClient,
    refetchPositionApplications,
  ]);

  const applications = useMemo(() => {
    const merged = filterApplicationQueue(
      unionPositionApplicationsList(positionApplicationsQuery.data, seededFromCompany),
      archived,
    );
    return archived ? merged : orderApplicationsForServerList(merged, 'updatedAt');
  }, [positionApplicationsQuery.data, seededFromCompany, archived]);

  const cachedPositionEmpty =
    positionApplicationsQuery.isSuccess
    && (positionApplicationsQuery.data?.length ?? 0) === 0
    && (seededFromCompany?.length ?? 0) > 0;

  const isLoading = shouldShowApplicationsListLoading({
    enabled,
    rowCount: applications.length,
    isPending: positionApplicationsQuery.isPending,
    isFetching: positionApplicationsQuery.isFetching,
    additionalFetching: cachedPositionEmpty,
  });

  const positionListMountKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !companyId || !positionId) return;
    createApplicationListSync(queryClient).ensurePositionListFresh(companyId, positionId, archived);
  }, [enabled, companyId, positionId, archived, queryClient]);

  /** Fresh listByPosition after process on company Applications (position cache can be stale). */
  useEffect(() => {
    if (!enabled || !companyId || !positionId || archived) return;
    const mountKey = `${companyId}:${positionId}`;
    if (positionListMountKeyRef.current === mountKey) return;
    positionListMountKeyRef.current = mountKey;
    void refetchPositionApplications();
  }, [
    enabled,
    companyId,
    positionId,
    archived,
    refetchPositionApplications,
  ]);

  return {
    applications,
    isLoading,
    isFetching: positionApplicationsQuery.isFetching,
    isSuccess: positionApplicationsQuery.isSuccess,
    refetch: positionApplicationsQuery.refetch,
  };
}

/** Cursor-paginated company applications (one page per fetch; Next loads more). */
export function useCompanyApplicationsInfiniteQuery(
  companyId: string | undefined,
  positions: JobPosition[],
  scope: CompanyApplicationsListScope,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const listKey = queryKeys.applicationsList(companyId ?? '', scope);

  return useInfiniteQuery({
    queryKey: listKey,
    queryFn: async ({ pageParam }) => {
      const freshPage = await api.applications.listByCompany(companyId!, positions, {
        limit: COMPANY_APPLICATIONS_FETCH_SIZE,
        cursor: pageParam as string | undefined,
        archived: scope.archived,
        state: scope.state,
        jobPositionId: scope.jobPositionId,
        orderBy: scope.orderBy,
      });
      const mergeOptions = { retainCacheOnlyIds: getRecentSettledRetainIds(companyId!) };
      const existing = queryClient.getQueryData<InfiniteData<CompanyApplicationsPage>>(listKey);
      if (!existing?.pages?.length) {
        return mergeFreshCompanyListPage(
          queryClient,
          companyId!,
          scope,
          freshPage,
          Date.now(),
          mergeOptions,
        );
      }

      const pageIndex = pageParam === undefined
        ? 0
        : existing.pageParams.findIndex((p) => p === pageParam);
      if (pageIndex < 0) return freshPage;

      const cachedItems = existing.pages[pageIndex]?.items ?? [];
      const overlay =
        pageIndex === 0
          ? mergeApplicationListsPreferNewest(
            cachedItems,
            collectCompanyListPageOverlay(queryClient, companyId!, scope),
          )
          : cachedItems;
      return {
        ...freshPage,
        items: mergeServerListPageWithOverlay(
          freshPage.items,
          overlay,
          Date.now(),
          mergeOptions,
        ),
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: !!companyId && enabled,
    ...listQueryOptions,
  });
}
